import { useState, useEffect } from "react";
import "./TemplateSupplierLetterUpload.scss";
import TemplateButton from "../Buttons/TemplateButton";
import { faArrowRightToArc } from "@fortawesome/pro-light-svg-icons";
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { useSecurityService } from "services";

interface Props {
    render: boolean;
    dismissCallback: (submitted: boolean, file?: File | null) => void;
    onFileSelected?: (valid: boolean) => void;
    cosmosGUID: string;
}

const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "image/png",
    "image/jpeg"
];

export default function TemplateSupplierLetterUpload({ render, dismissCallback, onFileSelected, cosmosGUID }: Props) {

    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);

    const { getAuthHeader, getUserEmail } = useSecurityService();

    useEffect(() => {
        if (render) {
            setFile(null);
            setError(null);
        }
    }, [render]);


    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0];

        if (selected) {
            if (allowedTypes.includes(selected.type)) {
                setFile(selected);
                setError(null);
                onFileSelected?.(true); // valid file selected
            } else {
                setFile(null);
                setError("Invalid File Type. Supported: PDF, Word, Excel, PNG, JPEG.");
                onFileSelected?.(false); // invalid file
            }
        } else {
            setFile(null);
            onFileSelected?.(false); // nothing selected
        }
    };

    const handleSubmit = async () => {
        if (!file) {
            dismissCallback(false, null);
            return;
        }

        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("cosmosGUID", cosmosGUID);

            const config: AxiosRequestConfig = {
                headers: {
                    ...(await getAuthHeader()),
                    "Content-Type": "multipart/form-data"
                }
            };

            console.log("Request for supplier letter being sent: ", formData)

            const response = await axios.post<{ message: string }>(
                process.env.REACT_APP_SALES_PRICINGIMPORT_UPLOAD_SUPPLIER_LETTER_API!,
                formData,
                config
            );

            console.log("Response for supplier letter upload: ", response)

            if (response.status !== 200) {
                throw new Error("Upload failed.");
            }

            dismissCallback(true, file);
        } catch (err) {
            console.error(err);
            setError("Upload failed. Please try again.");
            onFileSelected?.(false);
        }
    };

    return ( render && (
        <>
            <div className="overlay">
                <div className="supplierLetterPanel">
                    <p>Optional: Upload a Supplier Letter</p>
                    <div className="supplierLetterSelectorContainer">
                        <input
                            type="file"
                            accept={allowedTypes.join(',')}
                            onChange={handleFileChange}
                        />
                        {(!file && !error) && <p className="promptText">* Select a file</p>}
                        {file && <p className="uploadedText">* File Uploaded</p>}
                        {error && <p className="errorText">* {error}</p>}
                    </div>
                    <TemplateButton
                        className="continueButton"
                        text="SUBMIT"
                        btnIcon={faArrowRightToArc}
                        onClick={handleSubmit}
                    />
                </div>
            </div>
        </>
    ));
}

